import { Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Form, Stack, TextInput, TextArea } from '@carbon/react';
import { ImageField } from 'src/components/Storefront/ImageField';
import { StorefrontSeoDto } from 'src/services/apis/types';

/**
 * Unlike `ThemeEditor`'s untouched pass-through colour/font fields, every field here is edited,
 * so each one is declared — there is nothing meaningful to validate, but `toFormikValidationSchema`
 * still needs a schema shape to drive `isValid`.
 */
const Schema = z.object({
  title: z.string().nullish(),
  description: z.string().nullish(),
  ogImageFileId: z.string().nullish(),
});

/**
 * The generated `StorefrontSeoDto` types every field as optional `string`, but an unset image is
 * `null` everywhere it is held in the portal — that is what `ImageField` calls back with. The
 * field is narrowed here rather than widened to `any`, same as `StorefrontThemeValues`.
 */
export type StorefrontSeoValues = Omit<StorefrontSeoDto, 'ogImageFileId'> & {
  ogImageFileId?: string | null;
};

interface SeoEditorProps {
  seo: StorefrontSeoValues;
  onSave: (seo: StorefrontSeoValues) => void;
  saving: boolean;
}

/**
 * Sibling of `ThemeEditor`: same Formik + zod shape, same `null`-for-cleared convention, same
 * save-button treatment (disabled while saving or invalid, native form submit so Enter works).
 */
const SeoEditor = React.memo(({ seo, onSave, saving }: SeoEditorProps) => {
  const { t } = useTranslation();

  return (
    <Formik<StorefrontSeoValues>
      validationSchema={toFormikValidationSchema(Schema)}
      initialValues={{
        title: seo.title,
        description: seo.description,
        ogImageFileId: seo.ogImageFileId ?? null,
      }}
      onSubmit={(values) => onSave(values)}
    >
      {({ handleSubmit, values, setFieldValue, isValid }) => (
        <Form onSubmit={handleSubmit}>
          <Stack gap={6}>
            <TextInput
              id="seoTitle"
              labelText={t('storefront.appearance.seoTitle')}
              helperText={t('storefront.appearance.seoTitleHint')}
              value={values.title ?? ''}
              onChange={(e) => setFieldValue('title', e.target.value)}
            />

            <TextArea
              id="seoDescription"
              labelText={t('storefront.appearance.seoDescription')}
              helperText={t('storefront.appearance.seoDescriptionHint')}
              value={values.description ?? ''}
              onChange={(e) => setFieldValue('description', e.target.value)}
            />

            <ImageField
              title={t('storefront.appearance.ogImage')}
              description={t('storefront.appearance.ogImageHint')}
              aspectRatio={'2x1'}
              cropperAspectRatio={1200 / 630}
              value={values.ogImageFileId ?? null}
              onChange={(fileId) => setFieldValue('ogImageFileId', fileId)}
            />

            <Button type="submit" disabled={saving || !isValid}>
              {t('storefront.actions.save')}
            </Button>
          </Stack>
        </Form>
      )}
    </Formik>
  );
});

export { SeoEditor };
export default SeoEditor;
